import { ErrorHandler, Injectable } from '@angular/core'
import { CombinedError, SimpleWindowRefService } from '@ftr/foundation'
import { LoggingService } from './logging.service'

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService implements ErrorHandler {
  constructor(
    private loggingService: LoggingService,
    private windowRef: SimpleWindowRefService,
  ) {
    this.captureErrors()
  }

  handleError(error: any): void {
    console.error('Intercepted error:', error)

    if (isForbiddenLikeError(error) || isUserFixableCognitoError(error)) {
      return
    }

    this.loggingService.error({
      message: 'Unhandled error',
      source: 'ErrorHandlerService.handleError',
      error,
    })
  }

  private captureErrors(): void {
    this.windowRef.nativeWindow().onerror = (
      message: Event | string,
      url?: string,
      line?: number,
      column?: number,
      error?: Error,
    ) => {
      const errorContents = {
        message,
        source: 'window.onerror',
        file: url,
        line,
        column,
        error,
      }

      if (isErrorWithUsefulInformation(message)) {
        this.loggingService.error(errorContents)
      } else {
        this.loggingService.warn(errorContents)
      }
    }
  }
}

/**
 * Determines whether the given error message contains useful information.
 *
 * When a cross-origin script (e.g. an extension, Stripe, GA) errors, we do not have visibility into the
 * error that has been thrown. Instead we get an error object with 0 properties, or a single isTrusted=true
 * property when the error was thrown by an HTTPS script.
 *
 * If we come across one of these error objects with no properties, we consider it to not be useful. Otherwise
 * it is useful.
 */
function isErrorWithUsefulInformation(errorMessage: string | {}): boolean {
  return (
    (typeof errorMessage === 'string' && errorMessage.length > 0) ||
    Object.keys(errorMessage).filter(k => k !== 'isTrusted').length > 0
  )
}

// Matched by name rather than instanceof because @ftr/api-shared already depends on
// @ftr/ui-observability (JwtSocketService uses LoggingService), so we can't import
// ForbiddenApiError here without creating a cycle. The name is set explicitly in
// ForbiddenApiError's constructor - keep the two in sync.
const FORBIDDEN_API_ERROR_NAME = 'ForbiddenApiError'

function isForbiddenLikeError(error: unknown): boolean {
  if (error instanceof Error && error.name === FORBIDDEN_API_ERROR_NAME) {
    return true
  }
  if (error instanceof CombinedError) {
    return error.originalErrors.some(isForbiddenLikeError)
  }
  return false
}

// Cognito SDK exception names that CognitoAuthenticationService already handles at the UI
// (logs as warn, rejects with a friendly message shown on the login/MFA forms). The SDK
// re-surfaces the raw exception on a separate async path that lands in this global handler -
// that's just noise. Matching by `name` because these come from amazon-cognito-identity-js
// and we can't import them as classes.
const USER_FIXABLE_COGNITO_ERROR_NAMES: readonly string[] = [
  'NotAuthorizedException', // wrong password, login throttled, expired MFA session
  'UserNotFoundException', // email not registered
  'PasswordResetRequiredException', // must reset password
  'ExpiredCodeException', // MFA code expired
]

function isUserFixableCognitoError(error: unknown): boolean {
  return error instanceof Error && USER_FIXABLE_COGNITO_ERROR_NAMES.includes(error.name)
}
